import { isFilterMultiSelectNone, FILTER_MULTI_SELECT_NONE } from '../components/filterMultiSelectUtils';

export type CalendarSharedFilters = {
  mitarbeiter: string[];
  projekt: string[];
  typ: string[];
  status: string[];
  dateFrom: string | null;
  dateTo: string | null;
  search: string;
};

const STORAGE_KEY = 'meisterpfad_calendar_filters_v1';

type StoredCalendarFilters = {
  lastKey?: string;
  lastFilters?: CalendarSharedFilters;
  byKey: Record<string, CalendarSharedFilters>;
};

export const EMPTY_CALENDAR_FILTERS: CalendarSharedFilters = {
  mitarbeiter: [],
  projekt: [],
  typ: [],
  status: [],
  dateFrom: null,
  dateTo: null,
  search: '',
};

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

function normalizeFilters(value: unknown): CalendarSharedFilters | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  if (!isStringArray(row.mitarbeiter) || !isStringArray(row.projekt) || !isStringArray(row.typ)) {
    return null;
  }
  return {
    mitarbeiter: row.mitarbeiter,
    projekt: row.projekt,
    typ: row.typ,
    status: isStringArray(row.status) ? row.status : [],
    dateFrom: typeof row.dateFrom === 'string' || row.dateFrom === null ? (row.dateFrom as string | null) : null,
    dateTo: typeof row.dateTo === 'string' || row.dateTo === null ? (row.dateTo as string | null) : null,
    search: typeof row.search === 'string' ? row.search : '',
  };
}

function parseStore(raw: string | null): StoredCalendarFilters {
  if (!raw) return { byKey: {} };
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return { byKey: {} };
    const row = parsed as {
      lastKey?: unknown;
      lastFilters?: unknown;
      byKey?: unknown;
    };
    const byKey: Record<string, CalendarSharedFilters> = {};
    if (row.byKey && typeof row.byKey === 'object') {
      for (const [key, value] of Object.entries(row.byKey)) {
        const normalized = normalizeFilters(value);
        if (normalized) byKey[key] = normalized;
      }
    }
    const lastFilters = normalizeFilters(row.lastFilters) ?? undefined;
    return {
      byKey,
      lastKey: typeof row.lastKey === 'string' ? row.lastKey : undefined,
      lastFilters,
    };
  } catch {
    return { byKey: {} };
  }
}

export function calendarFiltersStorageKey(
  userId: string | null | undefined,
  instanceId: string | null | undefined,
): string | null {
  const u = String(userId ?? '').trim();
  const i = String(instanceId ?? '').trim();
  if (!u || !i) return null;
  return `${u}::${i}`;
}

/**
 * Sync read for first paint. When `instanceId` is known, only return filters stored for that
 * instance (any user key) so another instance’s selections never flash.
 */
export function readInitialCalendarFilters(instanceId?: string | null): CalendarSharedFilters {
  if (typeof localStorage === 'undefined') return { ...EMPTY_CALENDAR_FILTERS };
  const stored = parseStore(localStorage.getItem(STORAGE_KEY));
  const i = String(instanceId ?? '').trim();
  if (i) {
    const suffix = `::${i}`;
    for (const [key, filters] of Object.entries(stored.byKey)) {
      if (key.endsWith(suffix)) return { ...filters };
    }
    return { ...EMPTY_CALENDAR_FILTERS };
  }
  if (stored.lastKey && stored.byKey[stored.lastKey]) {
    return { ...stored.byKey[stored.lastKey] };
  }
  return { ...EMPTY_CALENDAR_FILTERS };
}

export function readCalendarFilters(
  userId: string | null | undefined,
  instanceId: string | null | undefined,
): CalendarSharedFilters {
  if (typeof localStorage === 'undefined') return { ...EMPTY_CALENDAR_FILTERS };
  const key = calendarFiltersStorageKey(userId, instanceId);
  if (!key) return { ...EMPTY_CALENDAR_FILTERS };
  const stored = parseStore(localStorage.getItem(STORAGE_KEY));
  if (stored.byKey[key]) return { ...stored.byKey[key] };
  return { ...EMPTY_CALENDAR_FILTERS };
}

/** Persist only when user+instance are known (avoids orphan lastFilters without byKey). */
export function writeCalendarFilters(
  userId: string | null | undefined,
  instanceId: string | null | undefined,
  filters: CalendarSharedFilters,
): void {
  if (typeof localStorage === 'undefined') return;
  const key = calendarFiltersStorageKey(userId, instanceId);
  if (!key) return;
  const stored = parseStore(localStorage.getItem(STORAGE_KEY));
  stored.lastKey = key;
  stored.lastFilters = filters;
  stored.byKey[key] = filters;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
}

/** Drop stale option ids that no longer exist (keeps NONE / emptyMeansAll intact). */
export function pruneFilterSelection(selected: string[], validIds: Set<string>): string[] {
  if (selected.length === 0) return selected;
  if (isFilterMultiSelectNone(selected)) return [FILTER_MULTI_SELECT_NONE];
  const next = selected.filter((id) => validIds.has(id));
  if (next.length === selected.length) return selected;
  // All selected ids went stale (e.g. week change) → fall back to "Alle", not "Keine".
  if (next.length === 0) return [];
  return next;
}
